import React from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Book, Terminal, Settings, Layers, Code, Zap, ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const menu = [
    { name: 'Getting Started', path: '/docs/getting-started', icon: <Zap className="w-4 h-4" /> },
    { name: 'Architecture', path: '/docs/architecture', icon: <Layers className="w-4 h-4" /> },
    { name: 'API Reference', path: '/docs/api', icon: <Terminal className="w-4 h-4" /> },
    { name: 'Plugin System', path: '/docs/plugins', icon: <Settings className="w-4 h-4" /> },
    { name: 'Contributing', path: '/docs/contributing', icon: <Code className="w-4 h-4" /> },
  ];

  return (
    <div className="w-64 border-r border-[#262626] bg-[#0A0A0A] flex flex-col pt-32 pb-20 px-4 sticky top-0 h-screen overflow-y-auto">
      <button 
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#404040] hover:text-white transition-colors mb-12 px-2 group"
      >
        <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
        Exit Docs
      </button>
      <div className="flex items-center gap-2 mb-12 px-2">
        <Book className="w-5 h-5 text-emerald-500" />
        <h2 className="text-[11px] font-bold uppercase tracking-widest text-white">Documentation</h2>
      </div>
      <div className="flex flex-col gap-2">
        {menu.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-xs font-bold uppercase tracking-widest",
              location.pathname === item.path ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "text-[#525252] hover:bg-white/5 hover:text-white"
            )}
          >
            {item.icon}
            {item.name}
          </Link>
        ))}
      </div>
    </div>
  );
};

const Content = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex-1 pt-32 pb-20 px-12 max-w-4xl"
  >
    <div className="mb-12">
      <div className="text-emerald-500 text-[10px] font-bold uppercase tracking-[0.5em] mb-4">Documentation</div>
      <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-white uppercase leading-none">{title}</h1>
    </div>
    <div className="prose prose-invert prose-emerald max-w-none font-mono text-sm text-[#A3A3A3] leading-relaxed space-y-8">
      {children}
    </div>
  </motion.div>
);

export const Docs: React.FC = () => {
  return (
    <div className="flex min-h-screen bg-[#0A0A0A]">
      <Sidebar />
      <Routes>
        <Route path="/" element={<Content title="Getting Started">
          <p>Oru Trace is a visual debugger for EVM transactions. It allows you to visualize, trace, and debug complex transaction execution flows with surgical precision.</p>
          {/* <h3 className="text-white uppercase tracking-widest text-lg font-bold mt-12 mb-4">Installation</h3>
          <pre className="bg-[#1A1A1A] p-6 rounded-xl border border-[#262626] text-emerald-500">
            npm install @oru-labs/trace
          </pre> */}
          <h3 className="text-white uppercase tracking-widest text-lg font-bold mt-12 mb-4">Basic Usage</h3>
          <p>The easiest way to use Oru Trace is through our web interface. Simply enter a transaction hash and select the network to start debugging.</p>
        </Content>} />
        <Route path="/getting-started" element={<Content title="Getting Started">
          <p>Oru Trace is a visual debugger for EVM transactions. It allows you to visualize, trace, and debug complex transaction execution flows with surgical precision.</p>
          {/* <h3 className="text-white uppercase tracking-widest text-lg font-bold mt-12 mb-4">Installation</h3>
          <pre className="bg-[#1A1A1A] p-6 rounded-xl border border-[#262626] text-emerald-500">
            npm install @oru-labs/trace
          </pre> */}
          <h3 className="text-white uppercase tracking-widest text-lg font-bold mt-12 mb-4">Basic Usage</h3>
          <p>The easiest way to use Oru Trace is through our web interface. Simply enter a transaction hash and select the network to start debugging.</p>
        </Content>} />
        <Route path="/architecture" element={<Content title="Architecture">
          <p>Oru Trace is built with a modular architecture that separates the trace engine, storage decoder, and visual components.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            <div className="p-8 rounded-2xl bg-[#0F0F0F] border border-[#262626]">
              <h4 className="text-white font-bold mb-4 uppercase tracking-widest text-sm">Trace Engine</h4>
              <p className="text-xs text-[#525252]">Consumes raw debug_traceTransaction data and builds a hierarchical call tree.</p>
            </div>
            <div className="p-8 rounded-2xl bg-[#0F0F0F] border border-[#262626]">
              <h4 className="text-white font-bold mb-4 uppercase tracking-widest text-sm">Storage Decoder</h4>
              <p className="text-xs text-[#525252]">Decodes storage mutations and resolves variable names via contract metadata.</p>
            </div>
          </div>
        </Content>} />
        <Route path="/api" element={<Content title="API Reference">
          <p>The Oru Trace API provides endpoints for retrieving normalized trace data and storage diffs.</p>
          <h4 className="text-white font-bold mt-12 mb-4 uppercase tracking-widest text-sm">GET /api/trace/:hash</h4>
          <p className="text-xs text-[#525252]">Returns a normalized trace for the given transaction hash.</p>
        </Content>} />
        <Route path="/plugins" element={<Content title="Plugin System">
          <p>Extend Oru Trace with custom visual components and chain-specific adapters using our modular plugin system.</p>
        </Content>} />
        <Route path="/contributing" element={<Content title="Contributing">
          <p>We welcome contributions from the community! Check out our GitHub repository to get started.</p>
        </Content>} />
      </Routes>
    </div>
  );
};
